import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatApiError } from '../../utils/errorMessage';
import { formatShortDate } from '../dashboard/calendarDate';
import { FORMULAS, formulaParts } from './drillLinks';

// Accounts behind Liquid Assets / Total Liabilities, and open invoices behind AR
// Outstanding. The total is computed by the same backend definition as the figure.
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v) || 0);

export function balanceParams(search) {
  const p = new URLSearchParams(search);
  const out = {};
  for (const k of ['kind', 'side', 'business_id', 'rollup', 'networth', 'formula', 'start_date', 'end_date']) if (p.get(k)) out[k] = p.get(k);
  return out;
}

function AccountRows({ rows }) {
  return (
    <table className="min-w-full text-sm">
      <thead><tr className="text-left text-gray-600"><th className="py-2 pr-4">Account</th><th className="pr-4">Type</th><th className="pr-4">Business</th><th className="text-right">Amount</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t">
            <td className="py-1 pr-4"><Link className="text-blue-700 hover:underline" to={`/accounts#account-${r.id}`}>{r.account_name}</Link></td>
            <td className="pr-4 capitalize">{String(r.account_type || '').replace('_', ' ')}</td>
            <td className="pr-4">{r.business_name || ''}</td>
            <td className="text-right whitespace-nowrap">{money(r.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const KIND_LABELS = { account: 'Account', vehicle: 'Vehicle', property: 'Property', asset: 'Other asset', receivable: 'Receivable' };
function AssetRows({ rows }) {
  return (
    <table className="min-w-full text-sm">
      <thead><tr className="text-left text-gray-600"><th className="py-2 pr-4">Type</th><th className="pr-4">Name</th><th className="text-right">Amount</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.kind}-${r.id}`} className="border-t">
            <td className="py-1 pr-4">{KIND_LABELS[r.kind] || r.kind}</td>
            <td className="pr-4">{r.name}</td>
            <td className="text-right whitespace-nowrap">{money(r.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvoiceRows({ rows }) {
  return (
    <table className="min-w-full text-sm">
      <thead><tr className="text-left text-gray-600"><th className="py-2 pr-4">Invoice</th><th className="pr-4">Customer</th><th className="pr-4">Due</th><th className="pr-4">Status</th><th className="text-right pr-4">Amount</th><th className="text-right pr-4">Paid</th><th className="text-right">Open</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t">
            <td className="py-1 pr-4"><Link className="text-blue-700 hover:underline" to="/accounts-receivable">{r.invoice_number || `#${r.id}`}</Link></td>
            <td className="pr-4">{r.customer_name}</td>
            <td className="pr-4 whitespace-nowrap">{formatShortDate(r.due_date)}</td>
            <td className="pr-4 capitalize">{String(r.status || '').replace('_', ' ')}</td>
            <td className="text-right pr-4">{money(r.amount)}</td>
            <td className="text-right pr-4">{money(r.amount_paid)}</td>
            <td className="text-right whitespace-nowrap">{money(r.open_amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Endpoint for one balance drill-down kind. */
export function balanceEndpoint(kind) {
  return `/dashboard/drilldown/${['ar', 'ar-projected', 'total-assets'].includes(kind) ? kind : 'accounts'}`;
}

// A figure worked out from other figures (Buying Power, Net Accounts, Net Worth,
// Equity): each part is loaded from its own drill-down, so the parts and their
// sum come from the same definitions as the dashboard.
export function FormulaDrill({ search }) {
  const f = balanceParams(search);
  const def = FORMULAS[f.formula];
  const [amounts, setAmounts] = useState(null);
  const [error, setError] = useState('');
  const scope = { business: f.business_id, rollup: f.rollup === 'true' };
  const parts = formulaParts(f.formula, scope);
  useEffect(() => {
    let live = true;
    setError(''); setAmounts(null);
    Promise.all(formulaParts(balanceParams(search).formula, {}).map((p) => {
      const { kind, ...params } = balanceParams(p.href.split('?')[1]);
      const base = balanceParams(search);
      if (base.business_id) params.business_id = base.business_id;
      if (base.rollup) params.rollup = base.rollup;
      return api.get(balanceEndpoint(kind), { params }).then((r) => Number(r.data?.total) || 0);
    }))
      .then((v) => { if (live) setAmounts(v); })
      .catch((e) => { if (live) setError(formatApiError(e, 'Could not load these records')); });
    return () => { live = false; };
  }, [search]);
  if (!def) return <p className="text-sm text-red-600">Unknown figure</p>;
  const total = amounts ? Math.round(parts.reduce((t, p, i) => t + p.sign * amounts[i], 0) * 100) / 100 : null;
  return (
    <div className="space-y-4">
      <Link to="/dashboard" className="text-sm text-blue-600">Back to dashboard</Link>
      <h2 className="text-2xl font-bold text-gray-900">{def.label}</h2>
      <p className="text-sm text-gray-600" data-testid="formula-text">{def.label} = {parts.map((p, i) => `${i === 0 ? '' : p.sign < 0 ? '- ' : '+ '}${p.label.toLowerCase()}`).join(' ')}</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead><tr className="text-left text-gray-600"><th className="py-2 pr-4">Part</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            {parts.map((p, i) => (
              <tr key={p.label} className="border-t">
                <td className="py-1 pr-4">{i === 0 ? '' : p.sign < 0 ? '- ' : '+ '}<Link className="text-blue-700 hover:underline" to={p.href}>{p.label}</Link></td>
                <td className="text-right whitespace-nowrap">{amounts ? money(amounts[i]) : '...'}</td>
              </tr>
            ))}
            <tr className="border-t font-semibold" data-testid="drilldown-total"><td className="py-1 pr-4">= {def.label}</td><td className="text-right whitespace-nowrap">{total === null ? '...' : money(total)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BalanceDrill({ search }) {
  if (balanceParams(search).kind === 'formula') return <FormulaDrill search={search} />;
  return <RecordsDrill search={search} />;
}

function RecordsDrill({ search }) {
  const f = balanceParams(search);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setError('');
    const { kind, ...params } = balanceParams(search);
    api.get(balanceEndpoint(kind), { params })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(formatApiError(e, 'Could not load these records')); });
    return () => { live = false; };
  }, [search]);
  return (
    <div className="space-y-4">
      <Link to="/dashboard" className="text-sm text-blue-600">Back to dashboard</Link>
      <h2 className="text-2xl font-bold text-gray-900">{data?.label || (f.kind === 'ar' ? 'AR outstanding' : 'Accounts')}</h2>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {data && (
        <>
          <div className="bg-white rounded-lg shadow p-4" data-testid="drilldown-total">
            <div className="text-sm text-gray-600">Total ({data.count} {['ar', 'ar-projected'].includes(data.kind) ? 'invoices' : data.kind === 'total-assets' ? 'items' : 'accounts'})</div>
            <div className="text-2xl font-bold">{money(data.total)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
            {['ar', 'ar-projected'].includes(data.kind) ? <InvoiceRows rows={data.rows} /> : data.kind === 'total-assets' ? <AssetRows rows={data.rows} /> : <AccountRows rows={data.rows} />}
          </div>
        </>
      )}
    </div>
  );
}
